import React, { useState } from 'react';
import { Search, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ManualEntry = ({ onRedeemSuccess }) => {
  const [voucherCode, setVoucherCode] = useState('');
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!voucherCode.trim()) return;

    setError('');
    setSuccess('');
    setLoading(true);
    setVoucher(null);

    try {
      const token = localStorage.getItem('business_token');
      const response = await axios.get(
        `${API}/api/business/vouchers/voucher/${voucherCode.toUpperCase()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVoucher(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Voucher not found');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('business_token');
      const response = await axios.post(
        `${API}/api/business/vouchers/redeem/${voucherCode.toUpperCase()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Voucher redeemed successfully!');
      setVoucher(response.data.voucher);
      onRedeemSuccess && onRedeemSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to redeem voucher');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setVoucherCode('');
    setVoucher(null);
    setError('');
    setSuccess('');
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Search Form */}
      {!voucher && (
        <form onSubmit={handleLookup} className="mb-6">
          <label className="block font-utility text-sm font-medium text-[#2C2416] mb-2">
            Enter Voucher Code
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
              placeholder="VRM-ABC123"
              className="flex-1 px-4 py-3 border-2 border-[#2C2416]/20 rounded-lg focus:ring-2 focus:ring-[#8B3A3A] focus:border-transparent font-mono text-lg"
              maxLength={20}
            />
            <button
              type="submit"
              disabled={loading || !voucherCode.trim()}
              className="bg-[#8B3A3A] text-[#F5F0E1] px-6 py-3 rounded-lg font-utility font-bold hover:bg-[#6B2A2A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : <Search size={20} />}
              Lookup
            </button>
          </div>
        </form>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 rounded-lg flex items-start gap-3">
          <XCircle className="text-red-600 flex-shrink-0" size={20} />
          <p className="font-utility text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 rounded-lg flex items-start gap-3">
          <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
          <p className="font-utility text-green-700">{success}</p>
        </div>
      )}

      {/* Voucher Details */}
      {voucher && (
        <div className="bg-white border-2 border-[#2C2416]/20 rounded-lg overflow-hidden">
          {/* Status Banner */}
          <div className={`p-4 ${
            voucher.status === 'active' ? 'bg-green-100' :
            voucher.status === 'redeemed' ? 'bg-blue-100' :
            'bg-red-100'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {voucher.status === 'active' && <CheckCircle className="text-green-600" />}
                {voucher.status === 'redeemed' && <CheckCircle className="text-blue-600" />}
                {voucher.status === 'expired' && <XCircle className="text-red-600" />}
                <span className="font-utility font-bold uppercase">
                  {voucher.status}
                </span>
              </div>
              <span className="font-mono font-bold text-lg">{voucher.voucher_code}</span>
            </div>
          </div>

          {/* Details */}
          <div className="p-6 space-y-4">
            <div>
              <h3 className="font-heading text-2xl font-bold text-[#2C2416] mb-1">
                {voucher.deal_title}
              </h3>
              <p className="font-utility text-[#2C2416]/70">{voucher.business_name}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InfoItem label="Customer" value={voucher.customer_name} />
              <InfoItem label="Amount Paid" value={`$${voucher.amount_paid.toFixed(2)}`} />
              <InfoItem label="Purchase Date" value={new Date(voucher.purchase_date).toLocaleDateString()} />
              <InfoItem label="Expires" value={new Date(voucher.expiration_date).toLocaleDateString()} />
            </div>

            {voucher.redemption_instructions && (
              <div className="p-4 bg-[#E8E2D2] rounded-lg">
                <p className="font-utility text-sm text-[#2C2416]/70 mb-1">Redemption Instructions:</p>
                <p className="font-utility text-[#2C2416]">{voucher.redemption_instructions}</p>
              </div>
            )}

            {voucher.status === 'redeemed' && voucher.redeemed_date && (
              <div className="p-4 bg-blue-100 rounded-lg">
                <p className="font-utility text-sm text-blue-700">
                  Redeemed on {new Date(voucher.redeemed_date).toLocaleString()}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4">
              {voucher.status === 'active' && (
                <button
                  onClick={handleRedeem}
                  disabled={loading}
                  className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-utility font-bold hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <CheckCircle size={20} />
                  )}
                  Redeem Voucher
                </button>
              )}
              <button
                onClick={handleReset}
                className="px-6 py-3 rounded-lg font-utility font-bold bg-[#E8E2D2] text-[#2C2416] hover:bg-[#D8D2C2] transition-colors"
              >
                Check Another
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <p className="font-utility text-xs text-[#2C2416]/60 mb-1">{label}</p>
    <p className="font-utility font-medium text-[#2C2416]">{value}</p>
  </div>
);

export default ManualEntry;
